import { AppConfig } from '@core/domain/AppConfig.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import { ApiSyncPullAdapter } from '@src/modules/system/infrastructure/driven/ApiSyncPullAdapter.ts';
import { BrowserSystemDiagnosticsAdapter } from '@src/modules/system/infrastructure/driven/BrowserSystemDiagnosticsAdapter.ts';
import { createAppMaintenancePort } from '@src/modules/system/infrastructure/driven/PwaAppMaintenanceAdapter.ts';
import { SqlJsCatalogPullMergeAdapter, type GetSystemDatabase } from '@src/modules/system/infrastructure/driven/SqlJsCatalogPullMergeAdapter.ts';
import { SqlJsSyncStateRepository } from '@src/modules/system/infrastructure/driven/SqlJsSyncStateRepository.ts';
import type { EntregaApplicationService } from '@src/modules/entrega-paquetes/application/EntregaApplicationService.ts';
import type { ReposicionApplicationService } from '@src/modules/reposicion-productos/application/ReposicionApplicationService.ts';
import type { SyncOutboundPort } from '@src/modules/system/domain/ports/SyncOutboundPort.ts';
import { runCombinedSyncPushLoop } from '@src/modules/platform/sync/application/runCombinedSyncPushLoop.ts';
import { createCombinedSyncPushAdapter, preferServiceToken } from '@src/modules/system/infrastructure/driven/apiSyncPushHelpers.ts';

export function createSyncOutboundPort(opts: {
  entrega: EntregaApplicationService;
  reposicion: ReposicionApplicationService;
  getToken: () => string | null;
  flushPersist?: () => Promise<void>;
  remoteAvailable?: boolean;
}): SyncOutboundPort {
  const remoteAvailable = opts.remoteAvailable ?? true;
  // Push (POST) targets the same Inclusion Social endpoint as pull, authenticated with the service BEARER_TOKEN.
  const pushPort = remoteAvailable
    ? createCombinedSyncPushAdapter(preferServiceToken(opts.getToken), AppConfig.getCatalogPushUrl())
    : null;

  return {
    listPending: async () => ({
      entregas: await opts.entrega.listUnsyncedSyncRows(),
      reposiciones: await opts.reposicion.listUnsyncedSyncRows(),
    }),
    pushAll: async (pushOpts) => {
      const { entregasAccepted, reposicionesAccepted, acceptedInferredFromTotales } =
        await runCombinedSyncPushLoop({
          pushPort,
          entregas: opts.entrega.combinedSyncPushSection(),
          reposiciones: opts.reposicion.combinedSyncPushSection(),
          flushPersist: opts.flushPersist,
          onProgress: pushOpts?.onProgress,
        });
      return {
        entregas: entregasAccepted.length,
        reposiciones: reposicionesAccepted.length,
        entregaMediaPurgeSkipped: opts.entrega.takeLastMediaPurgeSkipped(),
        reposicionMediaPurgeSkipped: opts.reposicion.takeLastMediaPurgeSkipped(),
        acceptedInferredFromTotales,
      };
    },
  };
}

export function createSystemStack(deps: {
  getDb: GetSystemDatabase;
  getToken: () => string | null;
  telemetry: TelemetryPort;
  entrega: EntregaApplicationService;
  reposicion: ReposicionApplicationService;
  flushPersist?: () => Promise<void>;
  remoteAvailable?: boolean;
}): SystemApplicationService {
  const remoteAvailable = deps.remoteAvailable ?? true;
  const referenceMerge = new SqlJsCatalogPullMergeAdapter(deps.getDb);
  // Local session tokens (`local.{id}`) are not remote credentials; pull/push use the service BEARER_TOKEN.
  const getServiceToken = preferServiceToken(deps.getToken);
  return new SystemApplicationService(
    new ApiSyncPullAdapter(getServiceToken),
    new SqlJsSyncStateRepository(deps.getDb),
    referenceMerge,
    new BrowserSystemDiagnosticsAdapter(() => deps.getToken() != null, remoteAvailable, deps.getDb),
    createAppMaintenancePort(),
    deps.telemetry,
    remoteAvailable,
    createSyncOutboundPort({
      entrega: deps.entrega,
      reposicion: deps.reposicion,
      getToken: deps.getToken,
      flushPersist: deps.flushPersist,
      remoteAvailable,
    }),
    deps.flushPersist,
  );
}
